/* 知識庫問答 — 右下浮動聊天室（gs-rag）
 *
 *  站內原生元件，取代原本「側欄項目 → 直開新分頁 /api/rag/」的入口
 *  （2026-08-17；原做法見 SPEC §10 的 2026-08-15 條目）。改成浮動泡泡的理由：
 *  問答是「邊看報表邊問」的輔助動作，開新分頁會逼使用者離開當前頁面。
 *
 *  掛載：_router.jsx 的 `cmdk` fragment（與 CommandPalette 同層）——
 *  兩個 return 分支都吃到，router 結構不用動。⚠️ 但這也意味著 dashboard 與
 *  其他路由之間切換時本元件會被 unmount/remount（兩分支的樹結構不同），
 *  所以對話紀錄**必須**外存，見下方 sessionStorage 段。
 *
 *  資料流（後端零改動，走既有的 routes_rag.py 代理）：
 *    GET /api/rag/api/ask?q=…   ← Bearer JWT（代理的 _proxy_user 先讀 header）
 *      → portal 代理（GET 放行、剝除 ?token=、JSON 原樣透傳、上游逾時 90s）
 *      → gs-rag GET /api/ask    （corpus 不帶 = 跨全部語料；top_k 用上游預設 6）
 *      → { text, grounded, mode, citations[], refused, scope } | { error }
 *
 *  刻意不用 hr-shell 的 api() helper：它不支援 AbortController，而本呼叫
 *  可能跑 30 秒以上（上游每次問答跑一次 LLM 生成），需要自己的逾時控制。
 *  錯誤 normalize 邏輯照抄 api()。
 *
 *  ⚠️ 員工限定：isRestrictedAccount(user) 為真（analyst / analyst_lite /
 *  non_staff）一律不 render。這與後端一致——`/api/rag` 不在三份非員工白名單，
 *  受限帳號打過去本來就會被閘門 default-deny，前端 gating 只是省一次 403。
 *  helper 不存在時視為受限（fail-closed）。
 *
 *  刻意不做：
 *    · 多輪上下文 —— 上游 ask() 只吃 q，把歷史串進 q 只會污染檢索。
 *      改用 placeholder 文案誠實告知「每則獨立檢索」。
 *    · streaming —— 上游一次回一整包 JSON，沒有 SSE/chunked 可用。
 *    · 語料選擇器 —— corpus 不帶即跨全部語料，多一個下拉換不到東西。
 *
 *  樣式：定位 / media query / keyframes / .rag-md / .rag-cite 定義在
 *  index.html 的全域 <style>（同 .ar-markdown 的做法），其餘走 inline style。
 */

/* sessionStorage：換頁與 F5 都保留，關分頁就清。刻意不用 localStorage——
 * 上游語料是公司內部知識庫，問答內容不該長期留在共用電腦的瀏覽器裡。 */
const RAG_STORE_KEY = 'rag.chat.v1';
const RAG_MAX_MSGS = 50;
// 前端逾時比代理的 90 秒略長，讓代理的 502 / 逾時訊息先到——否則前端會搶先
// abort，把真正的失敗原因蓋成一句「逾時」。
const RAG_TIMEOUT_MS = 95000;
// 上游本機 Ollama 約 1–2 秒，但生成器切成 claude CLI 時實測 32 秒（光 CLI
// 啟動就約 20 秒）。超過這個秒數補一句解釋，免得使用者以為當掉。
const RAG_SLOW_HINT_MS = 20;

const RAG_EXAMPLES = [
  'gs-repo-atlas 是做什麼的？',
  'portal 的薪資計算邏輯在哪個檔案？',
  'gs-rag 怎麼建索引？',
];

/* Expressive shell only: panel / FAB / markdown overrides for the index.html .rag-* rules
 * (Bento-shared, untouched). No-op under Bento; selectors scoped (tests/helpers/gx_css_guard.cjs). */
GxCore.injectCss('gx-css-p-rag', String.raw`
html[data-ui-shell="expressive"] .rag-fab{border-radius:16px;background:var(--gx-p);color:var(--gx-on-p);box-shadow:var(--gx-sh-fab);transition:border-radius .45s var(--gx-spring),transform .45s var(--gx-spring)}
html[data-ui-shell="expressive"] .rag-fab:hover{transform:none;border-radius:12px;box-shadow:var(--gx-sh-fab-hover)}
html[data-ui-shell="expressive"] .rag-fab:active{transform:scale(.94)}
html[data-ui-shell="expressive"] .rag-fab:focus-visible{outline:3px solid var(--gx-on);outline-offset:3px}
html[data-ui-shell="expressive"] .rag-panel{border:0;border-radius:16px;background:var(--gx-s1);box-shadow:var(--gx-sh-panel);font-family:var(--gx-font);animation:gx-rag-in .45s var(--gx-spring)}
@keyframes gx-rag-in{from{opacity:0;transform:translateY(16px) scale(.98)}}
html[data-ui-shell="expressive"] .rag-panel textarea:hover:not(:focus):not(:disabled){border-color:var(--gx-on-v)!important}
html[data-ui-shell="expressive"] .rag-panel textarea:focus{border-color:var(--gx-p)!important;outline:2px solid var(--gx-p)!important;outline-offset:-2px!important;background-color:var(--gx-card)!important;box-shadow:none!important}
html[data-ui-shell="expressive"] .rag-panel textarea::placeholder{color:var(--gx-faint);opacity:1}
html[data-ui-shell="expressive"] .rag-panel button:focus-visible{outline:3px solid var(--gx-p);outline-offset:2px}
html[data-ui-shell="expressive"] .rag-md{font-family:var(--gx-font);font-size:13.5px;color:var(--gx-on)}
html[data-ui-shell="expressive"] .rag-md a{color:var(--gx-p)}
html[data-ui-shell="expressive"] .rag-md hr{border-top-color:var(--gx-olv)}
html[data-ui-shell="expressive"] .rag-md code{border:0;border-radius:6px;background:var(--gx-s2)}
html[data-ui-shell="expressive"] .rag-md pre{border-radius:12px}
html[data-ui-shell="expressive"] .rag-md blockquote{border-left:0;border-radius:10px;background:var(--gx-pc);color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .rag-md :is(th,td){border-color:var(--gx-olv)}
html[data-ui-shell="expressive"] .rag-md thead th{background:var(--gx-s1)}
html[data-ui-shell="expressive"] .rag-cite{border-radius:6px;color:var(--gx-p);background:var(--gx-pc)}
html[data-ui-shell="expressive"] .rag-dot{background:var(--gx-mute)}
html[data-ui-shell="expressive"] .rag-scroll::-webkit-scrollbar-thumb{background:var(--gx-s4)}
@media (min-width:900px){
html[data-ui-shell="expressive"] body[data-rag-fab] main.gx-main{padding-bottom:56px}
}
@media (max-width:520px){
html[data-ui-shell="expressive"] .rag-panel{border-radius:0;height:100dvh;padding-bottom:env(safe-area-inset-bottom)}
}
@media (prefers-reduced-motion:reduce){
html[data-ui-shell="expressive"] .rag-fab,html[data-ui-shell="expressive"] .rag-panel{transition:none;animation:none}
html[data-ui-shell="expressive"] .rag-fab:active{transform:none}
}
`);

function ragLoad() {
  try {
    const raw = sessionStorage.getItem(RAG_STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-RAG_MAX_MSGS) : [];
  } catch (e) {
    return [];   // 壞掉的 JSON / private mode → 當空紀錄，不讓 widget 開不起來
  }
}

function ragSave(msgs) {
  try {
    sessionStorage.setItem(RAG_STORE_KEY, JSON.stringify(msgs.slice(-RAG_MAX_MSGS)));
  } catch (e) {
    /* quota / private mode 寫不進去 —— 記憶體內的對話照常運作 */
  }
}

function ragClearStore() {
  try { sessionStorage.removeItem(RAG_STORE_KEY); } catch (e) { /* ignore */ }
}

/* 內文的 [1] [2] 引用標記轉成上標 pill。
 * 負向前瞻 (?!\() 避開 markdown 連結語法 [1](url)——那是連結不是引用。
 * marked 會把這段 inline HTML 原樣放行，DOMPurify 預設保留 sup 與 class。 */
function ragMarkCitations(text) {
  return String(text == null ? '' : text)
    .replace(/\[(\d+)\](?!\()/g, '<sup class="rag-cite">$1</sup>');
}

/* 問答呼叫。錯誤 normalize 照 hr-shell 的 api()，但多帶 AbortSignal。
 * 不在 401 時清 token（api() 會清）——知識庫打不通不該連帶把整個 session
 * 砍掉，讓使用者自己決定要不要重登。 */
async function ragAsk(q, signal) {
  const headers = { 'Accept': 'application/json' };
  const token = (typeof getToken === 'function' && getToken()) || '';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/rag/api/ask?q=${encodeURIComponent(q)}`, {
    method: 'GET', headers, signal,
  });
  const text = await res.text();
  let data = null;
  if (text) { try { data = JSON.parse(text); } catch (e) { data = { raw: text }; } }
  if (!res.ok) {
    let detail = data && data.detail;
    if (typeof detail !== 'string') detail = '';
    throw { status: res.status, detail: detail || `HTTP ${res.status}` };
  }
  return data || {};
}

function ragErrText(e) {
  if (e && e.name === 'AbortError') return '回應逾時（95 秒），請重試。';
  const st = e && e.status;
  if (st === 401) return '連線已過期，請重新登入後再試。';
  if (st === 403) return '此功能限公司員工使用。';
  if (st === 502) return '知識庫問答服務暫時無法連線。';
  const d = e && e.detail;
  return d ? `發生錯誤：${d}` : '發生未預期的錯誤，請重試。';
}

/* ─────────────────────────  來源引用（預設收起）  ───────────────────── */

function RagCitationRow({ c }) {
  const label = [c && c.title, c && c.heading].filter(Boolean).join(' › ')
    || (c && c.doc_id) || '(無標題)';
  const url = (c && c.url) || '';
  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
    fontSize: FontSizes.trading_xl, color: url ? HR.accent : HR.inkSoft,
    textDecoration: 'none', fontFamily: HR.sans,
  };
  const body = (
    <>
      <span style={uiStyle({
        fontFamily: HR.mono, fontSize: FontSizes.trading_md, fontWeight: 600,
        color: HR.accent, background: HR.accentSoft, borderRadius: 3,
        padding: '0 4px', flexShrink: 0,
      }, { borderRadius: GxT.radius.chipSm, padding: '1px 6px', fontWeight: 700 })}>{c && c.n}</span>
      <span style={{
        flex: 1, minWidth: 0, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }} title={(c && c.source) || label}>{label}</span>
      {c && c.corpus && (
        <span style={uiStyle({
          fontSize: FontSizes.trading_md, color: HR.inkFaint,
          background: HR.ruleSoft, borderRadius: 3, padding: '0 4px', flexShrink: 0,
        }, { color: GxT.color.onV, background: GxT.color.s2, borderRadius: GxT.radius.chipSm, padding: '1px 6px' })}>{c.corpus}</span>
      )}
      {url && <Icon name="ext" size={10} />}
    </>
  );
  if (url) {
    return <a href={url} target="_blank" rel="noopener noreferrer" style={rowStyle}>{body}</a>;
  }
  return <div style={rowStyle}>{body}</div>;
}

function RagSources({ citations }) {
  const [open, setOpen] = React.useState(false);
  const list = Array.isArray(citations) ? citations : [];
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 8, borderTop: `1px solid ${HR.ruleSoft}`, paddingTop: 6 }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={uiStyle({
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontFamily: HR.sans, fontSize: FontSizes.trading_xl, color: HR.inkFaint,
          letterSpacing: '0.04em',
        }, { color: GxT.color.onV, fontWeight: 700, letterSpacing: 0, minHeight: 28 })}>
        來源 ({list.length}) {open ? '▴' : '▾'}
      </button>
      {open && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {list.map((c, i) => <RagCitationRow key={i} c={c} />)}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────  訊息氣泡  ───────────────────────────────── */

function RagUserBubble({ text }) {
  return (
    <div style={uiStyle({
      alignSelf: 'flex-end', maxWidth: '88%',
      background: HR.accent, color: '#fff',
      borderRadius: '10px 10px 3px 10px', padding: '8px 11px',
      fontFamily: HR.sans, fontSize: FontSizes.xs, lineHeight: 1.65,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }, { color: GxT.color.onP, borderRadius: '16px 16px 4px 16px', padding: '10px 14px', fontSize: 14 })}>{text}</div>
  );
}

function RagAssistantBubble({ m }) {
  const refused = !!m.refused;
  return (
    <div style={uiStyle({
      alignSelf: 'flex-start', maxWidth: '94%',
      background: refused ? '#FDF7EC' : HR.surfaceAlt,
      border: `1px solid ${refused ? '#EAD9B8' : HR.rule}`,
      borderLeft: refused ? `3px solid ${HR.warn}` : `1px solid ${HR.rule}`,
      borderRadius: '10px 10px 10px 3px', padding: '9px 11px',
    }, {
      background: refused ? GxT.tint.warn.bg : GxT.color.card,
      border: 'none', borderLeft: refused ? `3px solid ${GxT.color.amber}` : 'none',
      borderRadius: '16px 16px 16px 4px', padding: '10px 14px',
    })}>
      <MarkdownView
        className="rag-md"
        markdown={ragMarkCitations(m.text) || '（上游未回傳內容）'}
      />
      {/* grounded=false 且非拒答 = 有生成但沒有來源支撐，明講不要當事實用 */}
      {!refused && !m.grounded && (
        <div style={uiStyle({
          marginTop: 6, fontFamily: HR.sans,
          fontSize: FontSizes.trading_xl, color: HR.warn,
        }, { fontSize: 12.5, fontWeight: 700, color: GxT.tint.warn.fg })}>
          ⚠ 此回答缺乏來源支撐，請自行查證
        </div>
      )}
      {/* 拒答時不列來源——上游沒有採用任何來源，列出來會誤導 */}
      {!refused && <RagSources citations={m.citations} />}
    </div>
  );
}

function RagErrorBubble({ m, onRetry }) {
  return (
    <div style={uiStyle({
      alignSelf: 'flex-start', maxWidth: '94%',
      background: '#FBF0EF', border: `1px solid #EBD2D0`,
      borderLeft: `3px solid ${HR.down}`, borderRadius: '10px 10px 10px 3px',
      padding: '9px 11px', fontFamily: HR.sans,
      fontSize: FontSizes.xs, lineHeight: 1.65, color: HR.ink,
    }, {
      background: GxT.tint.down.bg, border: 'none', borderLeft: `3px solid ${GxT.color.down}`,
      borderRadius: '16px 16px 16px 4px', padding: '10px 14px', fontSize: 14, color: GxT.tint.down.fg,
    })}>
      {m.text}
      {m.retryQ && (
        <button
          onClick={() => onRetry(m.retryQ)}
          style={uiStyle({
            display: 'flex', alignItems: 'center', gap: 5, marginTop: 7,
            background: 'none', border: `1px solid ${HR.rule}`, borderRadius: 5,
            padding: '3px 8px', cursor: 'pointer',
            fontFamily: HR.sans, fontSize: FontSizes.trading_xl, color: HR.inkSoft,
          }, {
            gap: 6, marginTop: 8, background: GxT.color.card, border: 'none', borderRadius: GxT.radius.btnSm,
            minHeight: 32, padding: '0 12px', fontSize: 13, fontWeight: 700, color: GxT.tint.down.fg,
          })}>
          <Icon name="refresh" size={11} /> 重試
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────  主元件  ────────────────────────────────── */

function RagChatWidget({ user }) {
  const [open, setOpen] = React.useState(false);
  const [msgs, setMsgs] = React.useState(ragLoad);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const abortRef = React.useRef(null);

  React.useEffect(() => { ragSave(msgs); }, [msgs]);

  // 跨分頁登出（token 被清）→ 順手清掉問答紀錄，不留在瀏覽器裡。
  React.useEffect(() => {
    const onStorage = (e) => { if (e.key === 'token' && !e.newValue) ragClearStore(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  React.useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // 新訊息 / 進入等待 → 捲到底
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, busy, open]);

  // 等待秒數 —— 30 秒的等待沒有計時器會讓人以為壞了
  React.useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [busy]);

  // remount（換頁）時取消進行中的請求，避免 setState on unmounted
  React.useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  // fail-closed：helper 不存在時視為受限（gating 的 return 仍在所有 hook 之後，見下方）。
  const restricted = (typeof isRestrictedAccount === 'function')
    ? isRestrictedAccount(user) : true;

  // While the FAB exists, flag <body data-rag-fab> so the page reserves bottom room for it
  // (Bento: index.html `.va2-shell > main`; Expressive desktop: gx-css-p-rag `main.gx-main`) —
  // otherwise the fixed 56px button covers the last row, e.g. attendance pagination "下一頁 →".
  // Restricted accounts get no FAB, hence no extra space.
  React.useEffect(() => {
    if (restricted) return undefined;
    const body = document.body;
    body.setAttribute('data-rag-fab', '');
    return () => body.removeAttribute('data-rag-fab');
  }, [restricted]);

  const send = React.useCallback((raw) => {
    const q = String(raw == null ? '' : raw).trim();
    if (!q || busy) return;
    setDraft('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setMsgs(arr => [...arr, { role: 'user', text: q }]);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const timer = setTimeout(() => ctrl.abort(), RAG_TIMEOUT_MS);
    ragAsk(q, ctrl.signal)
      .then((data) => {
        // 上游用 200 + {error} 表達業務錯誤（缺 q / 沒註冊語料 / 未知語料）
        if (data && data.error) {
          setMsgs(arr => [...arr, { role: 'error', text: String(data.error), retryQ: q }]);
          return;
        }
        setMsgs(arr => [...arr, {
          role: 'assistant',
          text: (data && data.text) || '',
          citations: Array.isArray(data && data.citations) ? data.citations : [],
          grounded: !!(data && data.grounded),
          refused: !!(data && data.refused),
          scope: (data && data.scope) || '',
        }]);
      })
      .catch((e) => {
        const st = e && e.status;
        const retryable = !(st === 401 || st === 403);
        setMsgs(arr => [...arr, {
          role: 'error', text: ragErrText(e), retryQ: retryable ? q : null,
        }]);
        if (st === 403) setOpen(false);   // 前端 gating 沒擋住的話收合，別繼續誘導
      })
      .finally(() => {
        clearTimeout(timer);
        abortRef.current = null;
        setBusy(false);
      });
  }, [busy]);

  // ⚠️ gating 放在所有 hook 之後 —— 提前 return 會讓 hook 順序在 user
  // 載入前後不一致。
  if (restricted) return null;

  if (!open) {
    return (
      <button
        className="rag-fab" onClick={() => setOpen(true)}
        aria-label="開啟知識庫問答" title="知識庫問答（gs-rag）">
        <Icon name="message" size={22} />
      </button>
    );
  }

  return (
    <div className="rag-panel" role="dialog" aria-label="知識庫問答">
      {/* ── Header ── */}
      {/* Expressive: tonal header (pc / on-pc), title 800 — Bento keeps the ink bar. */}
      <div style={uiStyle({
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '11px 12px', background: HR.ink, color: '#fff', flexShrink: 0,
      }, { gap: 10, padding: '12px 10px 12px 16px', background: GxT.color.pc, color: GxT.color.onPc })}>
        <Icon name="book" size={uiPick(15, 18)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={uiStyle({
            fontFamily: HR.sans, fontSize: FontSizes.xs, fontWeight: 600,
            letterSpacing: '0.04em',
          }, { fontSize: 16, fontWeight: GxT.weight.title, letterSpacing: 0 })}>知識庫問答</div>
          <div style={uiStyle({
            fontFamily: HR.mono, fontSize: FontSizes.trading_md,
            color: 'rgba(255,255,255,0.55)', letterSpacing: '0.06em',
          }, { fontFamily: GxT.font, fontSize: 12, fontWeight: 600, color: GxT.color.onPc, letterSpacing: 0 })}>gs-rag · 帶來源引用</div>
        </div>
        {msgs.length > 0 && (
          <button
            onClick={() => { setMsgs([]); ragClearStore(); }}
            aria-label="清空對話" title="清空對話"
            style={uiStyle({
              background: 'none', border: 'none', padding: 4, cursor: 'pointer',
              color: 'rgba(255,255,255,0.55)', display: 'flex',
            }, { width: 40, height: 40, padding: 0, alignItems: 'center', justifyContent: 'center', borderRadius: GxT.radius.btn, color: GxT.color.onPc })}>
            <Icon name="trash" size={uiPick(14, 16)} />
          </button>
        )}
        <button
          onClick={() => setOpen(false)}
          aria-label="收合" title="收合（Esc）"
          style={uiStyle({
            background: 'none', border: 'none', padding: 4, cursor: 'pointer',
            color: 'rgba(255,255,255,0.7)', display: 'flex',
          }, { width: 40, height: 40, padding: 0, alignItems: 'center', justifyContent: 'center', borderRadius: GxT.radius.btn, color: GxT.color.onPc })}>
          <Icon name="x" size={uiPick(15, 18)} />
        </button>
      </div>

      {/* ── 訊息區 ── */}
      <div
        ref={scrollRef} className="rag-scroll" aria-live="polite"
        style={uiStyle({
          flex: 1, minHeight: 0, overflowY: 'auto', background: HR.surface,
          padding: '12px', display: 'flex', flexDirection: 'column', gap: 9,
        }, { background: GxT.color.s1, padding: '14px', gap: 10 })}>
        {msgs.length === 0 && !busy && (
          <div style={{ margin: 'auto 0', padding: '8px 2px' }}>
            <div style={uiStyle({
              fontFamily: HR.serif, fontSize: FontSizes.md,
              color: HR.ink, marginBottom: 6,
            }, { fontSize: 20, fontWeight: GxT.weight.title })}>問公司知識庫</div>
            <div style={uiStyle({
              fontFamily: HR.sans, fontSize: FontSizes.trading_xl,
              color: HR.inkFaint, lineHeight: 1.7, marginBottom: 12,
            }, { fontSize: 13, color: GxT.color.onV, marginBottom: 14 })}>
              回答只根據內部語料，並標註來源編號。找不到依據時會直接說沒有，不會編造。
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: uiPick(6, 8) }}>
              {RAG_EXAMPLES.map((q) => (
                <button
                  key={q} onClick={() => send(q)}
                  style={uiStyle({
                    textAlign: 'left', background: HR.surfaceAlt,
                    border: `1px solid ${HR.rule}`, borderRadius: 7,
                    padding: '7px 10px', cursor: 'pointer',
                    fontFamily: HR.sans, fontSize: FontSizes.trading_xl, color: HR.inkSoft,
                  }, {
                    background: GxT.color.card, border: 'none', borderRadius: GxT.radius.btn,
                    minHeight: 44, padding: '10px 14px', fontSize: 14, fontWeight: 600, color: GxT.color.on,
                  })}>{q}</button>
              ))}
            </div>
          </div>
        )}

        {msgs.map((m, i) => {
          if (m.role === 'user') return <RagUserBubble key={i} text={m.text} />;
          if (m.role === 'error') return <RagErrorBubble key={i} m={m} onRetry={send} />;
          return <RagAssistantBubble key={i} m={m} />;
        })}

        {busy && (
          <div style={uiStyle({
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
            background: HR.surfaceAlt, border: `1px solid ${HR.rule}`,
            borderRadius: '10px 10px 10px 3px', padding: '10px 12px',
          }, { background: GxT.color.card, border: 'none', borderRadius: '16px 16px 16px 4px', padding: '12px 14px' })}>
            <span style={{ display: 'flex', gap: 3 }}>
              <span className="rag-dot" /><span className="rag-dot" /><span className="rag-dot" />
            </span>
            <span style={uiStyle({
              fontFamily: HR.mono, fontSize: FontSizes.trading_md, color: HR.inkFaint,
            }, { color: GxT.color.onV, fontVariantNumeric: 'tabular-nums' })}>
              {elapsed > 0 ? `${elapsed}s` : ''}
            </span>
            {elapsed >= RAG_SLOW_HINT_MS && (
              <span style={uiStyle({
                fontFamily: HR.sans, fontSize: FontSizes.trading_md, color: HR.inkFaint,
              }, { color: GxT.color.onV })}>首次呼叫生成器較慢，請再等一下</span>
            )}
          </div>
        )}
      </div>

      {/* ── 輸入列 ── */}
      <div style={uiStyle({
        display: 'flex', alignItems: 'flex-end', gap: 7, flexShrink: 0,
        padding: '9px 10px', borderTop: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
      }, { gap: 8, padding: '10px 12px 12px', borderTop: `1px solid ${GxT.color.olv}`, background: GxT.color.s1 })}>
        <textarea
          ref={inputRef} value={draft} rows={1} disabled={busy}
          placeholder="問一個完整的問題（每則獨立檢索）"
          onChange={(e) => {
            setDraft(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(96, e.target.scrollHeight)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft); }
          }}
          style={uiStyle({
            flex: 1, minWidth: 0, resize: 'none', maxHeight: 96,
            border: `1px solid ${HR.rule}`, borderRadius: 7, padding: '7px 9px',
            fontFamily: HR.sans, fontSize: FontSizes.xs, lineHeight: 1.6,
            color: HR.ink, background: busy ? HR.ruleSoft : HR.surface, outline: 'none',
          }, {
            // outlined field (= hr-shell inputStyle under GX); busy = disabled → tonal s1 / olv / onV
            border: `1px solid ${busy ? GxT.color.olv : GxT.color.ol}`, borderRadius: GxT.radius.input, padding: '9px 12px',
            minHeight: 40, fontSize: 14, color: busy ? GxT.color.onV : GxT.color.on,
            background: busy ? GxT.color.s1 : GxT.color.card,
          })}
        />
        <button
          onClick={() => send(draft)} disabled={busy || !draft.trim()}
          aria-label="送出" title="送出（Enter）"
          style={uiStyle({
            flexShrink: 0, width: 32, height: 32, borderRadius: 7, border: 'none',
            display: 'grid', placeItems: 'center',
            background: (busy || !draft.trim()) ? HR.rule : HR.accent,
            color: (busy || !draft.trim()) ? HR.inkFaint : '#fff',
            cursor: (busy || !draft.trim()) ? 'default' : 'pointer',
          }, {
            width: 40, height: 40, borderRadius: GxT.radius.btn,
            background: (busy || !draft.trim()) ? GxT.color.s3 : GxT.color.p,
            color: (busy || !draft.trim()) ? GxT.color.onV : GxT.color.onP,
          })}>
          <Icon name="arrow-right" size={16} />
        </button>
      </div>
    </div>
  );
}

window.RagChatWidget = RagChatWidget;
